import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Star } from "lucide-react";

const TIERS = [
  {
    id: "hosting-plan-starter",
    name: "Starter Cloud",
    price: 29,
    specs: ["2 vCPU", "4 GB RAM", "50 GB NVMe", "Free SSL", "Daily Backups"],
    bestFor: "Startup landing pages & small business sites",
  },
  {
    id: "hosting-plan-pro",
    name: "Performance Pro",
    price: 89,
    popular: true,
    specs: ["6 vCPU", "16 GB RAM", "200 GB NVMe", "Global CDN", "Staging Environment", "Priority 24/7 Support"],
    bestFor: "High-traffic web apps & active marketing funnels",
  },
  {
    id: "hosting-plan-enterprise",
    name: "Enterprise Dedicated",
    price: 249,
    specs: ["16 vCPU", "64 GB RAM", "1 TB NVMe", "Isolated VPC", "Custom WAF", "Dedicated Infrastructure Engineer"],
    bestFor: "Mission-critical applications & multi-site networks",
  },
];

const Slider = ({ label, value, min, max, step, unit, onChange, testid }) => (
  <div>
    <div className="flex justify-between text-sm mb-2">
      <span className="font-medium text-slate-600 dark:text-slate-300">{label}</span>
      <span className="font-bold">{value}{unit}</span>
    </div>
    <input type="range" min={min} max={max} step={step} value={value}
           onChange={(e) => onChange(Number(e.target.value))} data-testid={testid}
           className="w-full accent-[#0256FF] h-2 cursor-pointer" />
  </div>
);

export const Hosting = () => {
  const [ram, setRam] = useState(16);
  const [vcpu, setVcpu] = useState(6);
  const [nvme, setNvme] = useState(200);
  const estimate = Math.round(19 + ram * 1.8 + vcpu * 4.5 + nvme * 0.08);

  return (
    <section id="hosting" className="py-20 lg:py-32 bg-slate-100/70 dark:bg-slate-900/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
                    className="max-w-2xl mb-14">
          <p className="font-semibold text-xs uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400 mb-4">Hosting Systems</p>
          <h2 className="font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">Hosting & Infrastructure Tiers</h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 dark:text-slate-300">
            Transparent, scalable cloud hosting packages engineered for peak workloads.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
          {TIERS.map((t, i) => (
            <motion.div key={t.id} data-testid={t.id}
                        initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay: i * 0.12 }}
                        className={`relative rounded-3xl border p-6 sm:p-8 flex flex-col transition-all duration-300 hover:shadow-2xl ${
                          t.popular
                            ? "bg-slate-950 dark:bg-slate-950 text-white border-primary shadow-xl shadow-blue-600/20 lg:-mt-4 lg:mb-[-1rem]"
                            : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-primary/50"
                        }`}>
              {t.popular && (
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary text-white text-xs font-bold uppercase tracking-wider">
                  <Star className="w-3 h-3 fill-current" /> Most Popular
                </span>
              )}
              <h3 className="font-bold text-lg sm:text-xl">{t.name}</h3>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-black text-4xl sm:text-5xl tracking-tight">${t.price}</span>
                <span className={`text-sm mb-1.5 ${t.popular ? "text-slate-400" : "text-slate-500 dark:text-slate-400"}`}>/mo</span>
              </div>
              <ul className="mt-6 space-y-2.5 flex-1">
                {t.specs.map((s) => (
                  <li key={s} className={`flex items-center gap-2 text-sm ${t.popular ? "text-slate-300" : "text-slate-600 dark:text-slate-300"}`}>
                    <Check className={`w-4 h-4 shrink-0 ${t.popular ? "text-cyan-400" : "text-primary"}`} /> {s}
                  </li>
                ))}
              </ul>
              <p className={`mt-6 text-xs ${t.popular ? "text-slate-400" : "text-slate-500 dark:text-slate-400"}`}>Best for: {t.bestFor}</p>
              <a href="#contact" data-testid={`${t.id}-cta`}
                 className={`mt-6 inline-flex justify-center px-6 py-3.5 rounded-full font-semibold text-sm transition-colors ${
                   t.popular ? "bg-primary text-white hover:bg-blue-600"
                             : "border-2 border-slate-900/80 dark:border-slate-200/70 hover:bg-slate-900 hover:text-white dark:hover:bg-white dark:hover:text-slate-900"
                 }`}>
                Choose {t.name}
              </a>
            </motion.div>
          ))}
        </div>

        <motion.div data-testid="hosting-calculator-slider"
                    initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}
                    className="mt-16 rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 sm:p-10 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h3 className="font-bold text-xl sm:text-2xl tracking-tight">Configure a Custom Server</h3>
            <p className="mt-3 text-sm sm:text-base text-slate-600 dark:text-slate-300">
              Slide to size your infrastructure and get an instant monthly estimate. Need something bespoke? Talk to us.
            </p>
            <div className="mt-8 space-y-7">
              <Slider label="RAM" value={ram} min={4} max={64} step={4} unit=" GB" onChange={setRam} testid="calc-ram-slider" />
              <Slider label="vCPU Cores" value={vcpu} min={2} max={16} step={1} unit="" onChange={setVcpu} testid="calc-vcpu-slider" />
              <Slider label="NVMe Storage" value={nvme} min={50} max={1000} step={50} unit=" GB" onChange={setNvme} testid="calc-nvme-slider" />
            </div>
          </div>
          <div className="rounded-3xl bg-slate-950 text-white p-8 sm:p-10 text-center relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-primary/30 blur-3xl" />
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-400">Estimated Monthly</p>
            <p className="mt-3 font-black text-5xl sm:text-6xl tracking-tight" data-testid="calc-estimate">${estimate}</p>
            <p className="mt-2 text-sm text-slate-400">{vcpu} vCPU · {ram} GB RAM · {nvme} GB NVMe</p>
            <a href="#contact" data-testid="calc-cta"
               className="mt-8 inline-flex px-7 py-3.5 rounded-full bg-primary text-white font-semibold text-sm hover:bg-blue-600 transition-colors">
              Talk to Us About This Setup
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
